import { Link } from "react-router-dom";
import { Phone, ArrowRight, MapPin, HandHeart, Home as HomeIcon, Users, Building2 } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { CtaBand } from "../components/CtaBand";
import { SITE, IMAGES, SERVICES, PROCESS_STEPS, WHO_WE_HELP } from "../data/site";

const WHO_ICONS = [HandHeart, HomeIcon, Users, Building2];

export default function HomePage() {
  usePageMeta(
    "MacWay Estate Services | Estate Sales, Cleanouts & Downsizing — DC, MD & VA",
    "Warm, professional help with estate sales, estate cleanouts, downsizing, packing, donation pickups, decluttering, junk removal, and judgment-free hoarding assistance across DC, Maryland, and Virginia. Call (301) 500-0128."
  );

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[82vh] flex items-center" data-testid="hero-section">
        <img
          src={IMAGES.hero}
          alt="MacWay team loading a truck during an estate cleanout"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/25" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 w-full">
          <div className="max-w-2xl">
            <p className="eyebrow-label text-brass animate-fade-up" data-testid="hero-eyebrow">
              Serving {SITE.serviceArea}
            </p>
            <h1
              className="mt-5 text-5xl sm:text-6xl font-semibold tracking-tight text-ivory leading-[1.05] animate-fade-up"
              style={{ animationDelay: "0.1s" }}
              data-testid="hero-headline"
            >
              {SITE.headline}
            </h1>
            <p
              className="mt-6 text-lg sm:text-xl text-ivory/85 leading-relaxed animate-fade-up"
              style={{ animationDelay: "0.2s" }}
              data-testid="hero-subtext"
            >
              When a house full of belongings feels overwhelming, MacWay steps in — with patient people, a clear
              plan, and a written quote before any work begins.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
              <Link to="/contact" className="btn-brass" data-testid="hero-quote-cta">
                Request a Quote
              </Link>
              <a href={SITE.phoneHref} className="btn-outline-light" data-testid="hero-call-cta">
                <Phone className="w-4 h-4" />
                Call {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Flash sale announcement — final liquidation weekend */}
      <section className="bg-sand border-y border-linen py-12 md:py-16" data-testid="home-sale-announcement">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="eyebrow-label" data-testid="announcement-eyebrow">
            Easton Flash Sale &nbsp;|&nbsp; November 7–8
          </p>
          <h2 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-forest" data-testid="announcement-heading">
            Furniture &amp; Carpets Must Go!
          </h2>
          <p className="mt-3 text-lg sm:text-xl font-bold uppercase tracking-[0.14em] text-brass-hover" data-testid="announcement-discounts">
            Deep Discounts! Everything Must Go!
          </p>
          <p className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed max-w-2xl mx-auto">
            We&rsquo;re back for one final weekend! MacWay Estate Services is returning to our beautiful Easton
            estate with incredible deals on remaining furniture, carpets, area rugs, upholstered seating, tables,
            artwork, home d&eacute;cor, and more.
          </p>
          <p className="mt-4 text-base sm:text-lg text-ink/70 leading-relaxed max-w-2xl mx-auto">
            <strong className="text-ink">We&rsquo;re priced to sell!</strong> Multiple-item purchases encouraged.
            Reasonable offers welcome.
          </p>
          <p className="mt-4 text-base sm:text-lg text-ink/70 leading-relaxed max-w-2xl mx-auto">
            <strong className="text-ink">Need delivery? We&rsquo;ve got you covered!</strong> Furniture and carpet
            delivery is available for an additional fee. Ask our team about delivery arrangements and pricing.
          </p>
          <p className="mt-5 text-sm font-bold uppercase tracking-[0.16em] text-ink/60" data-testid="announcement-dates">
            November 7–8, 2026 &nbsp;|&nbsp; Easton, Maryland
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-4">
            <Link to="/upcoming-estate-sales#gallery" className="btn-brass" data-testid="announcement-inventory-cta">
              View Flash Sale Inventory
            </Link>
            <Link to="/upcoming-estate-sales" className="btn-outline-dark" data-testid="announcement-cta">
              Flash Sale Details
            </Link>
          </div>
        </div>
      </section>

      {/* Services overview */}
      <section className="py-16 md:py-24 bg-ivory" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">What we do</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              One call handles the whole house
            </h2>
            <p className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed">
              From the first sort to the final sweep, MacWay covers every stage of emptying, selling, or
              simplifying a home — so you never have to coordinate three companies to finish one job.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={s.link}
                className="group bg-white border border-linen hover:border-forest/40 hover:shadow-md transition-all duration-300 hover:-translate-y-1 flex flex-col"
                data-testid={`service-card-${s.slug}`}
              >
                {s.image ? (
                  <div className="h-40 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="h-40 bg-forest flex items-center justify-center">
                    {s.slug === "junk-removal" ? (
                      <svg className="w-10 h-10 text-brass" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
                    ) : (
                      <HandHeart className="w-10 h-10 text-brass" />
                    )}
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-medium text-ink">{s.name}</h3>
                  {s.badge && (
                    <span
                      className="mt-2.5 inline-flex self-start items-center bg-brass/15 border border-brass/50 text-brass-hover px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em]"
                      data-testid={`service-badge-${s.slug}`}
                    >
                      {s.badge}
                    </span>
                  )}
                  <p className="mt-2.5 text-sm text-ink/65 leading-relaxed flex-1">{s.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-forest group-hover:text-brass transition-colors">
                    Learn more <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Who MacWay helps */}
      <section className="py-16 md:py-24 bg-sand" data-testid="home-who-we-help">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">Who we help</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              For families — and the professionals who guide them
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {WHO_WE_HELP.map((w, i) => {
              const Icon = WHO_ICONS[i % WHO_ICONS.length];
              return (
                <div key={w.title} className="bg-white border border-linen p-8" data-testid={`who-card-${i}`}>
                  <Icon className="w-8 h-8 text-brass" />
                  <h3 className="mt-5 text-2xl font-medium text-ink">{w.title}</h3>
                  <p className="mt-3 text-base text-ink/70 leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Working team / trust */}
      <section className="py-16 md:py-24 bg-ivory" data-testid="home-team">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img
              src={IMAGES.team2}
              alt="A MacWay mover unloading furniture with care"
              loading="lazy"
              className="w-full h-72 object-cover border border-linen"
            />
            <img
              src={IMAGES.truckCurb}
              alt="The MacWay truck at a home, ready for loading"
              loading="lazy"
              className="w-full h-72 object-cover border border-linen mt-8"
            />
          </div>
          <div>
            <p className="eyebrow-label">The MacWay way</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              Careful hands, clear communication
            </h2>
            <p className="mt-6 text-base sm:text-lg text-ink/70 leading-relaxed">
              We know we're not just moving things — we're handling a family's history. Our team treats every
              item and every home with respect, and we keep you informed from the first walkthrough to the last
              load out.
            </p>
            <ul className="mt-7 space-y-4">
              {[
                "A written quote before any work begins",
                "Donation-first thinking: useful items find new homes, not landfills",
                "Judgment-free help, whatever the condition of the property",
                "A local team serving DC, Maryland, and Virginia",
              ].map((point) => (
                <li key={point} className="flex items-start gap-3 text-base text-ink/75">
                  <span className="mt-1.5 w-2 h-2 bg-brass shrink-0" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
            <Link to="/about" className="btn-outline-dark mt-9" data-testid="home-about-cta">
              More about MacWay
            </Link>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-sand" data-testid="home-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">How it works</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              Three straightforward steps
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROCESS_STEPS.map((p) => (
              <div key={p.step} className="bg-white border border-linen p-8" data-testid={`process-step-${p.step}`}>
                <span className="font-serif text-5xl font-semibold text-brass">{p.step}</span>
                <h3 className="mt-4 text-2xl font-medium text-ink">{p.title}</h3>
                <p className="mt-3 text-base text-ink/70 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="py-16 md:py-24 bg-ivory" data-testid="home-service-area">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-forest text-ivory px-8 py-14 md:p-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="eyebrow-label">Service area</p>
              <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight">
                Local help across the DMV
              </h2>
              <p className="mt-5 text-base sm:text-lg text-ivory/80 leading-relaxed" data-testid="service-area-text">
                MacWay serves homeowners, families, and professionals throughout{" "}
                <strong className="text-ivory">Washington, DC, Maryland, and Northern Virginia</strong>. Not sure
                if you're in range? Call — if we can't help, we'll point you to someone who can.
              </p>
            </div>
            <div className="flex flex-col items-start lg:items-end gap-5">
              <p className="inline-flex items-center gap-3 text-lg font-semibold">
                <MapPin className="w-6 h-6 text-brass" />
                DC · Maryland · Virginia
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-brass" data-testid="service-area-quote-cta">Request a Quote</Link>
                <a href={SITE.phoneHref} className="btn-outline-light" data-testid="service-area-call-cta">
                  <Phone className="w-4 h-4" /> {SITE.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        title="Ready when you are."
        text="Whether you're settling an estate, preparing a listing, or helping a loved one into a smaller home — the first step is a simple conversation."
      />
    </main>
  );
}
